import { PointOfInterest, PointOfInterestTemplate } from '@/__generated__/graphql';
import { generatePoiDisplayName } from '../../Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { getFieldValues } from './getFieldValue';
import { usePoiStatusLabels } from '@/hooks/usePoiStatusLabels';

export const usePoiMarkerLabelData = ({
  pointOfInterest,
  height = 0,
  swivel = false,
  customTemplate,
}: {
  pointOfInterest: PointOfInterest;
  height?: number;
  scale?: number;
  swivel?: boolean;
  customTemplate?: Partial<PointOfInterestTemplate>;
}) => {
  const background = useAtomValue(state.poiMarkerLabelBgColor);
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const unitSystem = useAtomValue(state.unitSystem);
  const { getPoiStatusLabel } = usePoiStatusLabels();

  const selectedPoiTemplate = useMemo(() => {
    return (
      customTemplate ||
      allPointOfInterestTemplates.find((template) => template.id === pointOfInterest.templateId)
    );
  }, [allPointOfInterestTemplates, customTemplate, pointOfInterest.templateId]);

  const MarkerLabelFieldTypeEnumuration = useMemo(() => {
    return selectedPoiTemplate?.markerLabelConfig;
  }, [selectedPoiTemplate]);

  const hideName = useMemo(() => {
    return MarkerLabelFieldTypeEnumuration?.hideName;
  }, [MarkerLabelFieldTypeEnumuration]);

  const headerField = useMemo(() => {
    return MarkerLabelFieldTypeEnumuration?.headerField;
  }, [MarkerLabelFieldTypeEnumuration]);

  const poiName = useMemo(() => {
    return generatePoiDisplayName({
      name: pointOfInterest.name,
      poiId: pointOfInterest.poiId,
      poiType: pointOfInterest.type,
    });
  }, [pointOfInterest]);

  const barHeight = useMemo(() => height * 10 * (swivel ? 1.5 : 1), [height, swivel]);
  const randomAngle = useMemo(() => Math.random() * 75 * (Math.random() > 0.5 ? 1 : -1), []);

  const markerLabelFields = useMemo(() => {
    if (!MarkerLabelFieldTypeEnumuration?.fields || !selectedPoiTemplate) return [];

    return getFieldValues(
      MarkerLabelFieldTypeEnumuration.fields,
      pointOfInterest,
      selectedPoiTemplate,
      unitSystem,
      getPoiStatusLabel
    );
  }, [
    MarkerLabelFieldTypeEnumuration?.fields,
    pointOfInterest,
    selectedPoiTemplate,
    unitSystem,
    getPoiStatusLabel,
  ]);

  const headerFieldValue = useMemo(() => {
    if (!headerField || !selectedPoiTemplate) return null;
    return getFieldValues(
      [headerField],
      pointOfInterest,
      selectedPoiTemplate,
      unitSystem,
      getPoiStatusLabel
    )[0];
  }, [headerField, pointOfInterest, selectedPoiTemplate, unitSystem, getPoiStatusLabel]);

  const variant = useMemo(() => {
    return MarkerLabelFieldTypeEnumuration?.type;
  }, [MarkerLabelFieldTypeEnumuration]);

  return {
    name: poiName,
    background,
    barHeight,
    randomAngle,
    hideName,
    headerField,
    headerFieldValue,
    markerLabelFields,
    variant,
  };
};
